import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useLocation } from 'react-router-dom';
import { FaCog } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';

// Custom hooks
import { useTasks } from './hooks/useTasks';
import { useTaskFilters } from './hooks/useTaskFilters';
import { useEmployeeLocations } from './hooks/useEmployeeLocations';
import { useBodyScrollLock } from './hooks/useBodyScrollLock';

// Components
import TaskFilters from './components/Filters/TaskFilters';
import HeaderDateFilters from './components/Filters/HeaderDateFilters';
import TaskList from './components/TaskList/TaskList';
import TaskCard from './components/TaskCard/TaskCard';
import TaskListMap from './components/Maps/TaskListMap';
import TaskKanbanBoard from './components/Kanban/TaskKanbanBoard';
import TaskDetailsModal from './components/Modals/TaskDetailsModal';
import TaskCalendarView from './components/Calendar/TaskCalendarView';
import TaskGanttView from './components/Gantt/TaskGanttView';

// Modals
import CreateTaskModal from './components/Modals/CreateTaskModal/CreateTaskModal';
import CreateTodoModal from './components/Modals/CreateTodoModal/CreateTodoModal';
import CalendarAvailabilityModal from './components/Modals/CalendarAvailabilityModal';
import CreateSubtaskModal from './components/Modals/CreateSubtaskModal/CreateSubtaskModal';
import ActionModal from './components/Modals/ActionModal';
import SubtaskModal from './components/Modals/SubtaskModal';
import ImageModal from './components/Modals/ImageModal';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Modern Loading Component
const ModernLoadingState = () => (
  <div className="modern-loading-state">
    <div className="loading-spinner-container">
      <div className="loading-spinner modern"></div>
      <div className="loading-text">
        <h3>Loading Tasks</h3>
        <p>Please wait while we fetch your tasks...</p>
      </div>
    </div>
  </div>
);

// Enhanced Error State Component
const ErrorState = ({ error, onRetry }) => (
  <div className="error-state">
    <div className="error-icon" aria-hidden="true"><FiAlertTriangle /></div>
    <h3>Oops! Something went wrong</h3>
    <p>{error}</p>
    <button className="btn-primary" onClick={onRetry}>
      Try Again
    </button>
  </div>
);

const Tasks = () => {
  const { isAuthenticated, company, user } = useAuth();
  const location = useLocation();
  
  // Custom hooks
  const {
    tasks,
    loading,
    error,
    currentPage,
    totalPages,
    isLoadingMore,
    totalCount,
    setCurrentPage,
    fetchTasks,
    createTask,
    createSubtask,
    updateTask,
    refreshTasks
  } = useTasks();

  const {
    filterStatus,
    selectedDepartment,
    selectedEmployee,
    selectedCustomer,
    selectedSupervisor,
    showOverdueOnly,
    selectedDateRange,
    handleFilterChange,
    handleDepartmentChange,
    handleEmployeeChange,
    handleCustomerChange,
    handleSupervisorChange,
    handleOverdueFilterChange,
    handleDateFilterChange,
    clearFilters,
    getFilterChips
  } = useTaskFilters();

  // Prevent extra API calls: we'll only trigger fetch after URL params (if any) are parsed/applied.
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Apply filters from URL query params on initial load or when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // If any filter params exist, reset current filters first so that we apply params cleanly (prevents toggle inconsistencies when navigating from other pages)
    if (params.toString()) {
      clearFilters();
    }

    // Track whether we actually applied any param based filters
    let appliedAnyFilter = false;

    // ----- Status -----
    const statusParam = params.get('status'); // e.g. "in-progress,incoming" | "resolved"
    if (statusParam) {
      if (statusParam === 'all') {
        handleFilterChange('all');
      } else {
        statusParam
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((status) => handleFilterChange(status));
      }
      appliedAnyFilter = true;
    }

    // ----- Date range (startDate & endDate) -----
    const startDateParam = params.get('startDate'); // e.g. "2025-12-01"
    const endDateParam = params.get('endDate'); // e.g. "2025-12-31"
    if (startDateParam && endDateParam) {
      // Parse dates and create a range object
      const startDate = new Date(startDateParam);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(endDateParam);
      endDate.setHours(23, 59, 59, 999);
      // Create a label for the range
      const formatDate = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const label = startDateParam === endDateParam 
        ? formatDate(startDate)
        : `${formatDate(startDate)} - ${formatDate(endDate)}`;
      handleDateFilterChange({ startDate: startDate.toISOString(), endDate: endDate.toISOString(), label });
      appliedAnyFilter = true;
    } else {
      // ----- Date shortcut -----
      const dateParam = params.get('date'); // e.g. "today", "tomorrow", "this-week"
      if (dateParam) {
        handleDateFilterChange(dateParam);
        appliedAnyFilter = true;
      }
    }

    // ----- Additional filters (ids expected) -----
    const departmentId = params.get('departmentId');
    if (departmentId) {
      handleDepartmentChange(departmentId);
      appliedAnyFilter = true;
    }

    const employeeId = params.get('employeeId');
    if (employeeId) {
      handleEmployeeChange(employeeId);
      appliedAnyFilter = true;
    }

    const customerId = params.get('customerId');
    if (customerId) {
      handleCustomerChange(customerId);
      appliedAnyFilter = true;
    }

    const supervisorId = params.get('supervisorId');
    if (supervisorId) {
      handleSupervisorChange(supervisorId);
      appliedAnyFilter = true;
    }

    // ----- Overdue (boolean) -----
    const overdueParam = params.get('overdue'); // "1" | "true"
    if (overdueParam && ['1', 'true', 'yes'].includes(String(overdueParam).toLowerCase())) {
      handleOverdueFilterChange(true);
      appliedAnyFilter = true;
    }

    // Default supervisor to current user ("My Tasks") when none is specified via URL
    // Only default to current user if no other filters were applied via URL
    // This prevents forcing "My Tasks" when navigating from Dashboard with status/date filters.
    if (!appliedAnyFilter && !supervisorId && user?.id) {
      handleSupervisorChange(user.id);
    }

    // Mark filters as ready so the fetch effect can run once with the correct filter set.
    setCurrentPage(1);
    setFiltersInitialized(true);
  }, [location.search, clearFilters, handleFilterChange, handleDateFilterChange, handleDepartmentChange, handleEmployeeChange, handleCustomerChange, handleSupervisorChange, handleOverdueFilterChange, setCurrentPage, user?.id]);

  const { employeeLocations } = useEmployeeLocations();

  // UI state
  const [viewMode, setViewMode] = useState('kanban'); // 'list' | 'kanban' | 'calendar' | 'gantt'
  const [expandedTask, setExpandedTask] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [showTaskTypeDropdown, setShowTaskTypeDropdown] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateSubtaskModal, setShowCreateSubtaskModal] = useState(false);
  const [selectedTaskForSubtask, setSelectedTaskForSubtask] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubtaskSubmitting, setIsSubtaskSubmitting] = useState(false);

  // Data states
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  // In-memory availability (generic + specific)
  const [calendarAvailability, setCalendarAvailability] = useState({
    generic: {
      // day: [{ start:'09:00', end:'17:00' }]
      mon: [{ start: '09:00', end: '17:00' }],
      tue: [{ start: '09:00', end: '17:00' }],
      wed: [{ start: '09:00', end: '17:00' }],
      thu: [{ start: '09:00', end: '17:00' }],
      fri: [{ start: '09:00', end: '17:00' }],
      sat: [],
      sun: []
    },
    specific: [
      // { date: '2025-11-17', slots: [{ start:'10:00', end:'12:00' }] }
    ]
  });
  
  // Loading states for supporting data
  const [supportingDataLoading, setSupportingDataLoading] = useState(true);
  const [supportingDataError, setSupportingDataError] = useState(null);

  // Lock body scroll when any modal is open
  const isAnyModalOpen = showCreateModal || showTodoModal || showCreateSubtaskModal || showActionModal || showSubtaskModal || selectedImage;
  useBodyScrollLock(isAnyModalOpen);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showTaskTypeDropdown && !e.target.closest('.create-task-dropdown-container')) {
        setShowTaskTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTaskTypeDropdown]);

  // Ensure monthly filter when entering calendar or gantt view (must be before any early returns)
  useEffect(() => {
    if (viewMode !== 'calendar' && viewMode !== 'gantt') return;
    const base = selectedDateRange?.startDate ? new Date(selectedDateRange.startDate) : new Date();
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    first.setHours(0, 0, 0, 0);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0, 23, 59, 59, 999);
    handleDateFilterChange({
      startDate: first.toISOString(),
      endDate: last.toISOString(),
      label: first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    });
  }, [viewMode]);

  

  // Get current user's department IDs by searching supervisors list (preferred) or falling back to user object shape
  const getUserDepartmentIds = () => {
    if (!user) return [];

    // Prefer supervisors list if available (it contains rich department data)
    if (Array.isArray(supervisors) && supervisors.length > 0) {
      const me = supervisors.find((sup) => sup.id === user.id);
      console.log('me', me);
      if (me && Array.isArray(me.departments) && me.departments.length > 0) {
        return me.departments.map((d) => (typeof d === 'object' ? d.id ?? d : d));
      }
    }

   
    return [];
  };

  // Permission helper - allow if user is admin or belongs to the same department as the task (or task has no department yet)
  const hasDepartmentPermission = (task) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!task?.department?.id) return true; // allow assignment when task not yet assigned
    const userDeptIds = getUserDepartmentIds();
    return userDeptIds.includes(task.department.id);
  };

  // Fetch data when filters change, but only after we've initialized them (to avoid duplicate initial calls)
  useEffect(() => {
    if (!filtersInitialized) return;

    if (isAuthenticated && company?.id) {
      const filters = {
        status: filterStatus,
        departmentId: selectedDepartment,
        employeeId: selectedEmployee,
        customerId: selectedCustomer,
        supervisorId: selectedSupervisor,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate
      };
      fetchTasks(filters);
    }
  }, [filtersInitialized, isAuthenticated, company?.id, filterStatus, currentPage, selectedDepartment, selectedEmployee, selectedCustomer, selectedSupervisor, selectedDateRange]);

  // Fetch supporting data
  useEffect(() => {
    if (isAuthenticated && company?.id) {
      fetchSupportingData();
    }
  }, [isAuthenticated, company?.id]);

  const fetchSupportingData = async () => {
    setSupportingDataLoading(true);
    setSupportingDataError(null);
    
    try {
      const [departmentsResponse, employeesResponse, customersResponse] = await Promise.all([
        fetchDepartments(),
        fetchEmployees(),
        fetchCustomers(),
        fetchSupervisors()
      ]);
      
      // All requests completed successfully
      setSupportingDataLoading(false);
    } catch (err) {
      console.error('Error fetching supporting data:', err);
      setSupportingDataError('Failed to load supporting data. Some features may be limited.');
      setSupportingDataLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get(`/api/departments/${company.id}`);
      if (response.data?.is_success) {
        setDepartments(response.data.departments);
      }
      return response;
    } catch (err) {
      console.error('Error fetching departments:', err);
      throw err;
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get(`/api/companies/${company.id}/employees`);
      if (response.data?.is_success) {
        setEmployees(response.data.employees);
      }
      return response;
    } catch (err) {
      console.error('Error fetching employees:', err);
      throw err;
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get(`/api/companies/${company.id}/customers`);
      if (response.data?.is_success) {
        setCustomers(response.data.customers);
      }
      return response;
    } catch (err) {
      console.error('Error fetching customers:', err);
      throw err;
    }
  };

  const fetchSupervisors = async () => {
    try {
      const response = await api.get('/api/companies/users');
      if (response.data?.is_success) {
        // Filter to get only supervisors and admins
        const supervisorList = response.data.users.filter(user => 
          (user.role === 'admin' || user.role === 'supervisor') && user.isActive
        );
        
        // If current user is a supervisor (not admin), filter to show only supervisors in their departments
        if (user?.role === 'supervisor' && user.departments) {
          const userDepartmentIds = user.departments.map(dept => dept.id);
          const teamSupervisors = supervisorList.filter(supervisor => 
            supervisor.departments && supervisor.departments.some(dept => 
              userDepartmentIds.includes(dept.id)
            )
          );
          setSupervisors(teamSupervisors);
        } else {
          // If admin, show all supervisors
          setSupervisors(supervisorList);
        }
      }
      return response;
    } catch (err) {
      console.error('Error fetching supervisors:', err);
      throw err;
    }
  };

  // Event handlers
  const handleTaskClick = (task) => {
    setExpandedTask(expandedTask === task.id ? null : task.id);
    if (task.location?.coordinates?.lat && task.location?.coordinates?.lng) {
      setSelectedMarker(task.id);
    }
  };

  const handleActionClick = async (task, action) => {
    if (!hasDepartmentPermission(task)) {
      alert('You do not have permission to perform this action.');
      return;
    }
    // If user clicks resolve button while task is pending or incoming, treat as Start: set to in-progress immediately
    if (action === 'resolve' && ['pending','incoming'].includes(String(task.status).toLowerCase())) {
      await handleTaskUpdate(task.id, { status: 'in-progress', startedAt: new Date().toISOString() });
      // Refresh tasks list after status change
      const filters = {
        status: filterStatus,
        departmentId: selectedDepartment,
        employeeId: selectedEmployee,
        customerId: selectedCustomer,
        supervisorId: selectedSupervisor,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate
      };
      fetchTasks(filters);
      return;
    }
    // For non-pending tasks, open the action modal to resolve/reject/assign
    setSelectedTask(task);
    setActionType(action);
    setShowActionModal(true);
  };

  const handleSubtaskClick = (subtask) => {
    setSelectedSubtask(subtask);
    setShowSubtaskModal(true);
  };

  const handleCreateSubtask = (task) => {
    setSelectedTaskForSubtask(task);
    setShowCreateSubtaskModal(true);
  };

  const handleCreateSubtaskSubmit = async (subtaskData) => {
    setIsSubtaskSubmitting(true);
    const result = await createSubtask(selectedTaskForSubtask.id, subtaskData);
    setIsSubtaskSubmitting(false);
    
    if (result.success) {
      // Show success notification
      console.log('Subtask created successfully!');
      
      // Refresh the task list to show the updated parent task with new subtask
      const filters = {
        status: filterStatus,
        departmentId: selectedDepartment,
        employeeId: selectedEmployee,
        customerId: selectedCustomer,
        supervisorId: selectedSupervisor,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate
      };
      fetchTasks(filters);

      // Also refresh the selected task details if open
      if (showTaskDetailsModal && selectedTaskDetails?.id === selectedTaskForSubtask.id) {
        await refreshSelectedTaskDetails(selectedTaskForSubtask.id);
      }
    } else {
      // Show error notification
      alert(result.error);
    }
    
    return result;
  };

  const handleCreateTask = async (taskData) => {
    setIsSubmitting(true);
    const result = await createTask(taskData);
    setIsSubmitting(false);
    
    if (result.success) {
      // Refresh the task list
      const filters = {
        status: filterStatus,
        departmentId: selectedDepartment,
        employeeId: selectedEmployee,
        customerId: selectedCustomer,
        supervisorId: selectedSupervisor,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate
      };
      setCurrentPage(1);
      fetchTasks(filters); // Fetch all and let frontend paginate
      
      // Show success notification (you can implement a toast notification system)
      console.log('Task created successfully!');
    } else {
      // Show error notification
      alert(result.error);
    }
    
    return result;
  };

  const handleEditTask = async (taskData) => {
    setIsSubmitting(true);
    // Map form field names to backend expectations for updates
    const mapped = { ...taskData };
    // Normalize datetime fields to ISO (UTC) like create flow
    if (typeof mapped.dueDate === 'string') {
      mapped.dueDate = mapped.dueDate.trim() === '' ? null : new Date(mapped.dueDate).toISOString();
    }
    if (typeof mapped.followUpOn === 'string') {
      mapped.followUpOn = mapped.followUpOn.trim() === '' ? null : new Date(mapped.followUpOn).toISOString();
    }
    if (mapped.supervisorId !== undefined && mapped.supervisorId !== '') {
      mapped.supervisorAssigned = parseInt(mapped.supervisorId);
    }
    if (mapped.employeeId !== undefined && mapped.employeeId !== '') {
      mapped.employeeAssigned = parseInt(mapped.employeeId);
    }

    // Only change status to 'pending' if assignee actually changes
    if (mapped.status === undefined) {
      const prevSupervisorId = selectedTaskDetails?.supervisor?.id ?? selectedTaskDetails?.supervisorAssigned ?? null;
      const prevEmployeeId = selectedTaskDetails?.assignedEmployee?.id ?? selectedTaskDetails?.employeeAssigned ?? null;
      const nextSupervisorId = mapped.supervisorAssigned ?? prevSupervisorId;
      const nextEmployeeId = mapped.employeeAssigned ?? prevEmployeeId;
      const supervisorChanged = nextSupervisorId !== prevSupervisorId;
      const employeeChanged = nextEmployeeId !== prevEmployeeId;
      if (supervisorChanged || employeeChanged) {
        mapped.status = 'pending';
      }
    }
    // Remove UI-only keys
    delete mapped.supervisorId;
    delete mapped.employeeId;

    const result = await updateTask(selectedTaskDetails.id, mapped);
    setIsSubmitting(false);
    if (result.success) {
      const filters = {
        status: filterStatus,
        departmentId: selectedDepartment,
        employeeId: selectedEmployee,
        customerId: selectedCustomer,
        supervisorId: selectedSupervisor,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate
      };
      fetchTasks(filters);
      setShowEditModal(false);
      // Update the task details modal with the latest data if it's open
      if (showTaskDetailsModal && selectedTaskDetails?.id === result.task?.id) {
        setSelectedTaskDetails(result.task);
      }
    } else {
      alert(result.error);
    }
    return result;
  };

  const handleTaskUpdate = async (taskId, updateData) => {
    const result = await updateTask(taskId, updateData);
    
    if (result.success) {
      setShowActionModal(false);
      setSelectedTask(null);
      setActionType(null);
      
      // Show success notification
      console.log('Task updated successfully!');

      // If task details modal is open for this task, refresh it with updated data
      if (showTaskDetailsModal && selectedTaskDetails?.id === taskId && result.task) {
        setSelectedTaskDetails(result.task);
      }
    } else {
      alert(result.error);
    }
  };

  const handleFilterReset = () => {
    clearFilters();
    setCurrentPage(1);
    setSelectedMarker(null);
  };

  const handleRetry = () => {
    if (error) {
      // Retry main data fetch
      const filters = {
        status: filterStatus,
        departmentId: selectedDepartment,
        employeeId: selectedEmployee,
        customerId: selectedCustomer,
        supervisorId: selectedSupervisor,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate
      };
      fetchTasks(filters);
    }
    
    if (supportingDataError) {
      // Retry supporting data fetch
      fetchSupportingData();
    }
  };

  // Render functions
  const renderTaskItem = (task) => (
    <TaskCard
      key={task.id}
      task={task}
      isExpanded={expandedTask === task.id}
      isSelected={selectedMarker === task.id}
      onTaskClick={handleTaskClick}
      onImageClick={setSelectedImage}
      onSubtaskClick={handleSubtaskClick}
      onActionClick={handleActionClick}
      onCreateSubtask={handleCreateSubtask}
      departments={departments}
      employees={employees}
      employeeLocations={employeeLocations}
      onTaskAssignment={handleTaskAssignment}
      canModify={hasDepartmentPermission(task)}
    />
  );

  // Render for Kanban: do not expand, open modal instead
  const renderKanbanItem = (task) => (
    <TaskCard
      key={`kanban-${task.id}`}
      task={task}
      isExpanded={false}
      isSelected={false}
      onTaskClick={() => {
        setSelectedTaskDetails(task);
        setShowTaskDetailsModal(true);
      }}
      onImageClick={setSelectedImage}
      onSubtaskClick={handleSubtaskClick}
      onActionClick={handleActionClick}
      onCreateSubtask={handleCreateSubtask}
      departments={departments}
      employees={employees}
      employeeLocations={employeeLocations}
      onTaskAssignment={handleTaskAssignment}
      canModify={hasDepartmentPermission(task)}
    />
  );

  // Task assignment handler for Action Center
  const handleTaskAssignment = async (taskId, assignmentData, parentId) => {
    try {
      const result = await updateTask(taskId, assignmentData);
      
      if (result.success) {
        // Show success notification
        console.log('Task assigned successfully!');
        
        // Refresh the task list to show updated assignment
      const filters = {
          status: filterStatus,
          departmentId: selectedDepartment,
          employeeId: selectedEmployee,
          customerId: selectedCustomer,
          supervisorId: selectedSupervisor,
          startDate: selectedDateRange?.startDate,
          endDate: selectedDateRange?.endDate
        };
      fetchTasks(filters);

      // If a parentId was provided (subtask updated), refresh parent details in modal
      if (parentId && showTaskDetailsModal && selectedTaskDetails?.id === parentId) {
        await refreshSelectedTaskDetails(parentId);
      }
      } else {
        throw new Error(result.error || 'Failed to assign task');
      }
    } catch (error) {
      console.error('Error assigning task:', error);
      throw error; // Re-throw so ActionCenter can handle the error
    }
  };

  // Helper to refresh currently viewed task details
  const refreshSelectedTaskDetails = async (taskId) => {
    try {
      const response = await api.get(`/api/complaints/${taskId}`);
      if (response.data?.is_success) {
        setSelectedTaskDetails(response.data.complaint);
      }
    } catch (err) {
      console.error('Failed to refresh task details', err);
    }
  };

  // Authentication check
  if (!isAuthenticated) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
            <h2 style={{ fontSize: 20, margin: 0 }}>Tasks</h2>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}></div>
          </div>
          <div className="auth-required-state">
            <div className="auth-icon">🔐</div>
            <h2>Authentication Required</h2>
            <p>Please log in to access your tasks</p>
          </div>
        </div>
      </div>
    );
  }

  // Main loading state
  if (loading && !tasks.length) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
            <h2 style={{ fontSize: 20, margin: 0 }}>Tasks</h2>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}></div>
          </div>
          <ModernLoadingState />
        </div>
      </div>
    );
  }

  // Client-side pagination
  const PAGE_SIZE = 10;
  const now = new Date();
  const allowOverdueFilter = viewMode === 'kanban' || viewMode === 'list';
  const isOverdueFilterActive = Boolean(showOverdueOnly && allowOverdueFilter);

  const filteredTasks = isOverdueFilterActive
    ? (Array.isArray(tasks) ? tasks.filter((t) => {
        if (!t?.dueDate) return false;
        const due = new Date(t.dueDate);
        if (Number.isNaN(due.getTime())) return false;
        const status = String(t.status || '').toLowerCase();
        if (status === 'resolved' || status === 'rejected') return false;
        return due < now;
      }) : [])
    : (Array.isArray(tasks) ? tasks : []);

  const effectiveTotal = (typeof totalCount === 'number' && !isOverdueFilterActive) ? totalCount : filteredTasks.length || 0;
  const localTotalPages = Math.max(1, Math.ceil(effectiveTotal / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const pagedTasks = filteredTasks.slice(startIndex, endIndex);

  return (
    <div className="page-container" style={{ overflowX: 'hidden' }}>
      <div className="page-content" style={{ overflowX: 'hidden' }}>
        <div className="tasks-header">
          <div className="tasks-header-top">
            <h2>Tasks</h2>
            <div className="tasks-header-actions">
             
              <HeaderDateFilters 
                value={selectedDateRange}
                onChange={handleDateFilterChange}
              />
              <div className="create-task-dropdown-container" style={{ position: 'relative' }}>
                <button 
                  className="submit-button"
                  onClick={() => setShowTaskTypeDropdown(!showTaskTypeDropdown)}
                  disabled={isSubmitting}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="button-loading-spinner"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Task
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 2 }}>
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </>
                  )}
                </button>
                {showTaskTypeDropdown && (
                  <div 
                    className="task-type-dropdown"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      right: 0,
                      background: '#ffffff',
                      border: '1px solid rgba(203, 213, 225, 0.6)',
                      borderRadius: 10,
                      boxShadow: '0 8px 24px rgba(2, 6, 23, 0.12)',
                      minWidth: 200,
                      zIndex: 100,
                      overflow: 'hidden'
                    }}
                  >
                    <button
                      onClick={() => {
                        setShowTaskTypeDropdown(false);
                        setShowTodoModal(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>Quick Todo</span>
                      <span style={{ fontSize: 11, color: '#64748b' }}>Simple task with basic fields</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowTaskTypeDropdown(false);
                        setShowCreateModal(true);
                        if (!customers.length && !supportingDataLoading) {
                          fetchCustomers();
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>Full Task</span>
                      <span style={{ fontSize: 11, color: '#64748b' }}>Complete task with all options</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                className="submit-button"
                onClick={() => setShowCalendarModal(true)}
                title="Customer Connect"
                aria-label="Customer Connect"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <FaCog aria-hidden="true" />
                Customer Connect
              </button>
            </div>
          </div>
          <div className="tasks-header-bottom">
            <div className="view-toggle" role="tablist" aria-label="View toggle">
              <button 
                className={`btn-secondary small ${viewMode === 'list' ? 'active' : ''}`}
                role="tab"
                aria-selected={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                List + Map
              </button>
              <button 
                className={`btn-secondary small ${viewMode === 'kanban' ? 'active' : ''}`}
                role="tab"
                aria-selected={viewMode === 'kanban'}
                onClick={() => setViewMode('kanban')}
              >
                Kanban
              </button>
              <button 
                className={`btn-secondary small ${viewMode === 'calendar' ? 'active' : ''}`}
                role="tab"
                aria-selected={viewMode === 'calendar'}
                onClick={() => setViewMode('calendar')}
              >
                Calendar
              </button>
              <button 
                className={`btn-secondary small ${viewMode === 'gantt' ? 'active' : ''}`}
                role="tab"
                aria-selected={viewMode === 'gantt'}
                onClick={() => setViewMode('gantt')}
              >
                Gantt
              </button>
            </div>
          </div>
        </div>
        {/* Supporting Data Error Banner */}
        {supportingDataError && (
          <div className="error-banner">
            <span className="error-icon" aria-hidden="true"><FiAlertTriangle /></span>
            <span>{supportingDataError}</span>
            <button 
              className="btn-secondary small" 
              onClick={() => fetchSupportingData()}
              disabled={supportingDataLoading}
            >
              {supportingDataLoading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        )}

        <div className="tasks-content" style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          {error ? (
            <ErrorState error={error} onRetry={handleRetry} />
          ) : (
            <div className="tasks-layout" style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
              {/* Filters at the top */}
              <div className="filters-top-container">
                <TaskFilters 
                  filters={{
                    filterStatus,
                    selectedDepartment,
                    selectedEmployee,
                    selectedCustomer,
                    selectedSupervisor,
                    showOverdueOnly,
                    selectedDateRange
                  }}
                  onFilterChange={{
                    handleFilterChange,
                    handleDepartmentChange,
                    handleEmployeeChange,
                    handleCustomerChange,
                    handleSupervisorChange,
                    handleOverdueFilterChange,
                    handleDateFilterChange
                  }}
                  departments={departments}
                  employees={employees}
                  customers={customers}
                  supervisors={supervisors}
                  filterChips={getFilterChips(departments, employees, customers, user, supervisors).filter((chip) => chip?.type !== 'overdue' || allowOverdueFilter)}
                  onClearAll={handleFilterReset}
                  isLoading={supportingDataLoading}
                  currentUser={user}
                  allowOverdueFilter={allowOverdueFilter}
                />
              </div>

              {/* Task List and Map View side by side */}
              {viewMode === 'list' ? (
                <div className="content-bottom-container">
                  {/* Task List */}
                  <TaskList 
                    tasks={pagedTasks}
                    totalCount={isOverdueFilterActive ? filteredTasks.length : totalCount}
                    loading={loading}
                    isLoadingMore={isLoadingMore}
                    currentPage={currentPage}
                    totalPages={localTotalPages}
                    onPageChange={setCurrentPage}
                    renderTaskItem={renderTaskItem}
                    error={error}
                    onRefresh={refreshTasks}
                  />

                  {/* Map View */}
                  <div className="map-view-container">
                    <TaskListMap 
                      tasks={filteredTasks}
                      selectedMarker={selectedMarker}
                      handleTaskClick={(task) => {
                        setSelectedTaskDetails(task);
                        setShowTaskDetailsModal(true);
                      }}
                      setSelectedImage={setSelectedImage}
                      employeeLocations={employeeLocations}
                    />
                  </div>
                </div>
              ) : viewMode === 'kanban' ? (
                <TaskKanbanBoard 
                  tasks={filteredTasks}
                  loading={loading}
                  totalCount={isOverdueFilterActive ? filteredTasks.length : totalCount}
                  onTaskClick={(task) => {
                    setSelectedTaskDetails(task);
                    setShowTaskDetailsModal(true);
                  }}
                />
              ) : viewMode === 'calendar' ? (
                <div className="content-bottom-container" style={{ display: 'block' }}>
                  <TaskCalendarView
                    tasks={filteredTasks}
                    selectedDateRange={selectedDateRange}
                    onChangeDateRange={handleDateFilterChange}
                    onTaskClick={(task) => {
                      setSelectedTaskDetails(task);
                      setShowTaskDetailsModal(true);
                    }}
                  />
                </div>
              ) : (
                <div className="content-bottom-container" style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
                  <TaskGanttView
                    tasks={filteredTasks}
                    selectedDateRange={selectedDateRange}
                    onChangeDateRange={handleDateFilterChange}
                    onTaskClick={(task) => {
                      setSelectedTaskDetails(task);
                      setShowTaskDetailsModal(true);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateTaskModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTask}
        departments={departments}
        employees={employees}
        customers={customers}
        employeeLocations={employeeLocations}
        isSubmitting={isSubmitting}
      />

      <CreateTodoModal
        isOpen={showTodoModal}
        onClose={() => setShowTodoModal(false)}
        onSubmit={handleCreateTask}
        onUpdate={updateTask}
        departments={departments}
        employees={employees}
        isSubmitting={isSubmitting}
      />

      <CreateSubtaskModal 
        isOpen={showCreateSubtaskModal}
        onClose={() => {
          setShowCreateSubtaskModal(false);
          setSelectedTaskForSubtask(null);
        }}
        onSubmit={handleCreateSubtaskSubmit}
        parentTask={selectedTaskForSubtask}
        departments={departments}
        employees={employees}
        customers={customers}
        employeeLocations={employeeLocations}
        isSubmitting={isSubtaskSubmitting}
      />

      <CalendarAvailabilityModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        availability={calendarAvailability}
        onSave={(next) => {
          setCalendarAvailability(next);
        }}
      />

      <ActionModal 
        isOpen={showActionModal}
        onClose={() => {
          setShowActionModal(false);
          setSelectedTask(null);
          setActionType(null);
        }}
        selectedTask={selectedTask}
        actionType={actionType}
        departments={departments}
        employees={employees}
        employeeLocations={employeeLocations}
        onAction={handleTaskUpdate}
      />

      <SubtaskModal 
        selectedSubtask={selectedSubtask}
        onClose={async () => {
          setShowSubtaskModal(false);
          // Refresh parent task details if details modal is open
          if (showTaskDetailsModal && selectedTaskDetails?.id) {
            await refreshSelectedTaskDetails(selectedTaskDetails.id);
          }
          setSelectedSubtask(null);
        }}
        departments={departments}
        employees={employees}
        employeeLocations={employeeLocations}
        onTaskAssignment={handleTaskAssignment}
        onImageClick={setSelectedImage}
      />

      <TaskDetailsModal
        isOpen={showTaskDetailsModal}
        task={selectedTaskDetails}
        onClose={() => {
          setShowTaskDetailsModal(false);
          setSelectedTaskDetails(null);
        }}
        onActionClick={handleActionClick}
        onCreateSubtask={handleCreateSubtask}
        onSubtaskClick={handleSubtaskClick}
        onImageClick={setSelectedImage}
        onEditClick={(task) => {
          setSelectedTaskDetails(task);
          setShowEditModal(true);
        }}
        departments={departments}
        employees={employees}
        employeeLocations={employeeLocations}
        canModifyTask={(t) => hasDepartmentPermission(t)}
      />

      {/* Edit Task Modal (reuses CreateTaskModal in edit mode) */}
      <CreateTaskModal 
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditTask}
        departments={departments}
        employees={employees}
        customers={customers}
        employeeLocations={employeeLocations}
        isSubmitting={isSubmitting}
        initialTaskData={selectedTaskDetails}
        mode="edit"
      />

      {/* Render the image modal last so it stacks above other modals */}
      <ImageModal 
        selectedImage={selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  );
};

export default Tasks; 